import { Link, TableCell, Typography } from "@mui/material";
import {
  createSearchRequestProvider,
  getActiveFiltersCount,
  preprocessSearchRequest,
  withoutBaseTableModel,
} from "~/components/Table";
import {
  commonResourceColumns,
  selectCountableListResponse,
} from "~/domain/common";
import type { LqsColumn, ObjectStore } from "~/lqs";
import { LqsResourceTable, useObjectStores } from "~/lqs";
import {
  DataStoreLink,
  makeNewObjectStoreLocation,
  makeObjectStoreLocation,
} from "~/paths";
import { listObjectStoresSchema } from "../schemas";

const columns: ReadonlyArray<LqsColumn<ObjectStore>> = [
  {
    header: "Bucket Name",
    sortKey: "bucketName",
    renderCell(objectStore) {
      return (
        <TableCell>
          {objectStore.bucketName}
          <Link
            component={DataStoreLink}
            to={makeObjectStoreLocation({ objectStoreId: objectStore.id })}
          >
            <Typography variant="body2">{objectStore.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  {
    accessor: "accessKeyId",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "regionName",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "endpointUrl",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "disabled",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "note",
    dataType: "text",
    isSortable: true,
  },
  ...commonResourceColumns,
];

export const [useObjectStoreSearchRequest, ObjectStoreSearchRequestProvider] =
  createSearchRequestProvider({
    columns,
    filterSchema: listObjectStoresSchema,
  });

export function ObjectStoreTable() {
  const [request, setRequest] = useObjectStoreSearchRequest();

  const searchQuery = useObjectStores(preprocessSearchRequest(request), {
    keepPreviousData: true,
    cacheTime: 0,
    select: selectCountableListResponse,
  });

  const filterValues = withoutBaseTableModel(request);

  return (
    <LqsResourceTable
      resourceName="object store"
      resourceCreateLocation={makeNewObjectStoreLocation()}
      getRowKey={(objectStore) => objectStore.id}
      columns={columns}
      searchQuery={searchQuery}
      tableModel={request}
      onTableModelChange={setRequest}
      activeFilterCount={getActiveFiltersCount(filterValues)}
    />
  );
}
